import React from 'react'
import styled from 'styled-components/macro'
import {
  compose,
  variant,
  buttonStyle,
  grid,
  position,
  space,
  GridProps,
  PositionProps,
  SpaceProps,
  LayoutProps,
  layout,
  flexbox,
  FlexboxProps,
} from 'styled-system'
import { themeGet } from '@styled-system/theme-get'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { PERMISSIONS } from 'src/config/roleConfig'
import {
  ResourceWithAuthz,
  useCanPerformActionChecker,
  useHasRequiredPermissions,
} from 'src/app/AppAuth/AppAuth.hooks'
import { Spinner } from 'src/Throbber'
import {
  Tooltip,
  TooltipOverlayType,
  TooltipPositions,
} from '../../Tooltip/Tooltip'

const buttonSize = variant({
  prop: 'buttonSize',
  variants: {
    small: {
      height: '32px',
      px: 's',
      fontSize: '1',
      fontWeight: 'semiBold',
      borderRadius: '8px',
    },
    medium: {
      height: '40px',
      px: 'm',
      fontSize: '3',
      fontWeight: 'semiBold',
      borderRadius: '8px',
    },
    large: {
      height: '48px',
      px: 'l',
      fontSize: '4',
      fontWeight: 'semiBold',
      borderRadius: '8px',
    },
  },
})

export enum StandardButtonVariant {
  PRIMARY = 'primary',
  SECONDARY = 'cancel',
  TERTIARY = 'tertiary',
}

export enum StandardButtonSize {
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
}

export type StandardButtonProps = React.DetailedHTMLProps<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
> &
  LayoutProps &
  FlexboxProps &
  GridProps &
  PositionProps &
  SpaceProps & {
    variant: StandardButtonVariant
    buttonSize: StandardButtonSize
  }

export const StandardButton = styled.button.attrs<StandardButtonProps>(
  ({ display, alignItems, justifyContent }) => ({
    display: display || 'flex',
    alignItems: alignItems || 'center',
    justifyContent: justifyContent || 'center',
  }),
)<StandardButtonProps>`
  ${compose(buttonStyle, buttonSize)}
  ${compose(layout, flexbox, grid, position, space)}
    
    ${(props) => {
    if (Array.isArray(props.children) && props.children.length > 1)
      return `
        > :first-child {
          margin-right: ${themeGet('space.xs')(props)}px;
        }
      `
  }}
    white-space: nowrap;
`

export const PrimaryButton = ({
  text,
  onClick = () => undefined,
  isDisabled,
  isLoading,
  disabledReason,
  requiredPermissions = [PERMISSIONS.READ, PERMISSIONS.EDIT],
  requiredCapabilities,
  resource,
  size = StandardButtonSize.MEDIUM,
  buttonType = 'button',
  marginBottom,
}: {
  text: string
  onClick?: () => void
  isDisabled?: boolean
  isLoading?: boolean
  disabledReason?: string
  requiredPermissions?: PERMISSIONS[]
  /** Domain-specific authority for this action. Prefer this over legacy permissions. */
  requiredCapabilities?: CAPABILITY[]
  resource?: ResourceWithAuthz
  size?: StandardButtonSize
  buttonType?: 'button' | 'submit' | 'reset'
  marginBottom?: number
}): JSX.Element => {
  const canPerformAction = useCanPerformActionChecker()
  const hasPermission = useHasRequiredPermissions(requiredPermissions)
  const hasCapabilities =
    requiredCapabilities?.every((capability) =>
      canPerformAction(capability, resource),
    ) ?? true
  const isAuthorized = requiredCapabilities ? hasCapabilities : hasPermission

  let tooltipText = disabledReason
  let isButtonDisabled = isDisabled
  if (!isAuthorized) {
    tooltipText = 'You do not have permission to perform this action.'
    isButtonDisabled = true
  }

  return (
    <>
      <Tooltip
        id={text}
        tooltipBody={tooltipText}
        disabled={!isButtonDisabled || !tooltipText}
        overlayType={TooltipOverlayType.popover}
        placement={TooltipPositions.top}
      >
        <div>
          <StandardButton
            type={buttonType}
            buttonSize={size}
            variant={StandardButtonVariant.PRIMARY}
            marginBottom={marginBottom === undefined ? 10 : marginBottom}
            onClick={onClick}
            disabled={isButtonDisabled || isLoading}
          >
            {isLoading ? <Spinner size="s" color="busy" /> : text}
          </StandardButton>
        </div>
      </Tooltip>
    </>
  )
}

export const SecondaryButton = ({
  text,
  onClick,
  isDisabled,
  disabledReason,
  icon,
  size = StandardButtonSize.MEDIUM,
  requiredPermissions = [PERMISSIONS.READ, PERMISSIONS.EDIT],
  requiredCapabilities,
  resource,
  content,
  marginBottom,
}: {
  text?: string
  onClick: () => void
  isDisabled?: boolean
  icon?: JSX.Element
  disabledReason?: string
  size?: StandardButtonSize
  requiredPermissions?: PERMISSIONS[]
  requiredCapabilities?: CAPABILITY[]
  resource?: ResourceWithAuthz
  content?: JSX.Element
  marginBottom?: number
}): JSX.Element => {
  const canPerformAction = useCanPerformActionChecker()
  const hasPermission = useHasRequiredPermissions(requiredPermissions)
  const hasCapabilities =
    requiredCapabilities?.every((capability) =>
      canPerformAction(capability, resource),
    ) ?? true
  const isAuthorized = requiredCapabilities ? hasCapabilities : hasPermission

  let tooltipText = disabledReason
  let isButtonDisabled = isDisabled
  if (!isAuthorized) {
    tooltipText = 'You do not have permission to perform this action.'
    isButtonDisabled = true
  }

  return (
    <>
      <Tooltip
        id={text || 'secondary-button'}
        tooltipBody={tooltipText}
        disabled={!isButtonDisabled || !tooltipText}
        overlayType={TooltipOverlayType.popover}
        placement={TooltipPositions.top}
      >
        <StandardButton
          type="button"
          buttonSize={size}
          variant={StandardButtonVariant.SECONDARY}
          marginBottom={marginBottom === undefined ? 10 : marginBottom}
          onClick={onClick}
          disabled={isButtonDisabled}
        >
          {icon}
          {text}
          {content}
        </StandardButton>
      </Tooltip>
    </>
  )
}

export const TertiaryButton = ({
  text,
  onClick,
  isDisabled,
  disabledReason,
  icon,
  size = StandardButtonSize.MEDIUM,
  requiredPermissions = [PERMISSIONS.READ, PERMISSIONS.EDIT],
  requiredCapabilities,
  resource,
  content,
  marginBottom,
}: {
  text?: string
  onClick: (e?: React.MouseEvent) => void
  isDisabled?: boolean
  icon?: JSX.Element
  disabledReason?: string
  size?: StandardButtonSize
  requiredPermissions?: PERMISSIONS[]
  requiredCapabilities?: CAPABILITY[]
  resource?: ResourceWithAuthz
  content?: JSX.Element
  marginBottom?: number
}): JSX.Element => {
  const canPerformAction = useCanPerformActionChecker()
  const hasPermission = useHasRequiredPermissions(requiredPermissions)
  const hasCapabilities =
    requiredCapabilities?.every((capability) =>
      canPerformAction(capability, resource),
    ) ?? true
  const isAuthorized = requiredCapabilities ? hasCapabilities : hasPermission

  let tooltipText = disabledReason
  let isButtonDisabled = isDisabled
  if (!isAuthorized) {
    tooltipText = 'You do not have permission to perform this action.'
    isButtonDisabled = true
  }

  return (
    <>
      <Tooltip
        id={text || 'secondary-button'}
        tooltipBody={tooltipText}
        disabled={!isButtonDisabled || !tooltipText}
        overlayType={TooltipOverlayType.popover}
        placement={TooltipPositions.top}
      >
        <StandardButton
          type="button"
          buttonSize={size}
          variant={StandardButtonVariant.TERTIARY}
          marginBottom={marginBottom === undefined ? 10 : marginBottom}
          onClick={onClick}
          disabled={isButtonDisabled}
        >
          {icon}
          {text}
          {content}
        </StandardButton>
      </Tooltip>
    </>
  )
}
